// One candidate in the Shift Detail panel: the shared worker identity block plus the fit score and
// an Assign action (eligible candidates only). On open slots, eligible rows also show a KPI delta
// (coverage + FTE before/after), computed from real roster hours + cell demand. The identity layout
// lives in ScheduleShiftDetailWorkerIdentity so it can't drift from the assigned-worker row.
import React from 'react';
import { Button, Divider, Gap, Gutter, Tag } from '@common/components';
import type {
  ScheduleDragStateType,
  ScheduleTeamMemberType,
} from '@load-balancing/types';
import type { ScheduleShiftCandidateRowType } from '@load-balancing/graphql/queries/shift-candidates';
import { startDragGhost } from '@load-balancing/services';
import {
  candidateKpi,
  reasonsToSignals,
} from './_schedule-shift-detail-panel-service';
import { ScheduleShiftDetailWorkerIdentity } from './_schedule-shift-detail-worker-identity';

export function ScheduleShiftDetailCandidateRow({
  candidate,
  member,
  showKpi = false,
  coverage = null,
  durationHours = 0,
  assigning = false,
  onAssign,
  onWorkerSelect,
  onWorkerDragStart,
  onWorkerDragEnd,
}: {
  candidate: ScheduleShiftCandidateRowType;
  member: ScheduleTeamMemberType;
  /** Show the coverage/FTE delta chips — open slots only. */
  showKpi?: boolean;
  /** Cell coverage base for the KPI delta (open slots). */
  coverage?: { before: number; target: number } | null;
  /** Shift length in hours, for the FTE delta. */
  durationHours?: number;
  /** An assignShift is in flight — disable Assign so a second click can't double-submit. */
  assigning?: boolean;
  onAssign?: (memberId: string) => void;
  onWorkerSelect?: (memberId: string) => void;
  /** Drag this candidate onto an open grid slot (a new assignment). */
  onWorkerDragStart?: (state: ScheduleDragStateType) => void;
  onWorkerDragEnd?: () => void;
}) {
  // Ineligible workers with hours left read "Xh left", not "Xh to FTE" — the latter implies they're
  // still short of their cap when the real blocker is shift-specific.
  const fteLabel =
    !candidate.eligible &&
    member.remainingHours !== null &&
    member.remainingHours > 0
      ? `${member.remainingHours}h left`
      : undefined;

  // A candidate's applied constraints become its row tags (hard block vs soft penalty).
  const signals = reasonsToSignals(candidate.reasons);

  const kpi =
    showKpi && candidate.eligible
      ? candidateKpi(member.remainingHours, coverage, durationHours)
      : null;
  const footer = kpi ? (
    <Gap size="xs" wrap>
      <Tag kind="square" color="grassLightest" textColor="grassDarkest">
        Coverage {kpi.coverage.before} → {kpi.coverage.after} of{' '}
        {kpi.coverage.target}
      </Tag>
      <Tag kind="square" color="steelLighter" textColor="midnightLighter">
        {kpi.fteRemaining.before} → {kpi.fteRemaining.after}h to FTE
      </Tag>
    </Gap>
  ) : undefined;

  const handleDragStart = (event: React.DragEvent) => {
    startDragGhost(event, member.name);
    onWorkerDragStart?.({
      workerId: member.id,
      workerName: member.name,
      source: 'fit',
    });
  };

  const handleDragEnd = () => onWorkerDragEnd?.();

  const assignButton = candidate.eligible && (
    <Gutter direction="top" size="xxs">
      <Gap size="sm" align="center">
        {/* @TODO: reenable it to the second release */}
        {/* {typeof candidate.fitScore === 'number' && (
                  <Text
                    size="md"
                    weight="medium"
                    color={scoreColor(candidate.fitScore)}
                  >
                    {candidate.fitScore}
                  </Text>
                )} */}
        {onAssign && (
          <Button
            kind="secondary"
            size="sm"
            disabled={assigning}
            onClick={() => onAssign(member.id)}
          >
            Assign
          </Button>
        )}
      </Gap>
    </Gutter>
  );

  return (
    <>
      <div
        draggable={onWorkerDragStart ? true : undefined}
        style={onWorkerDragStart ? { cursor: 'grab' } : undefined}
        onDragStart={onWorkerDragStart ? handleDragStart : undefined}
        onDragEnd={onWorkerDragStart ? handleDragEnd : undefined}
      >
        <Gap size="sm" align="start" justify="space-between" fullWidth>
          <ScheduleShiftDetailWorkerIdentity
            actionSlot={assignButton}
            name={member.name}
            unit={member.unit}
            role={member.role}
            member={member}
            signals={signals}
            fteLabel={fteLabel}
            footer={footer}
            dragHandle={Boolean(onWorkerDragStart)}
            onClick={
              onWorkerSelect ? () => onWorkerSelect(member.id) : undefined
            }
          />
        </Gap>
      </div>
      <Divider secondary />
    </>
  );
}
